import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Instagram, Building2, FileText, Briefcase, Headphones } from "lucide-react";
import { BRANCHES, COMPANY } from "../lib/bakeryData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        branch: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Please fill in your name, email, and message.");
            return;
        }
        try {
            setLoading(true);
            await axios.post(`${API}/contact`, form);
            toast.success("Thank you! We'll get back to you soon.");
            setForm({ name: "", email: "", phone: "", branch: "", message: "" });
        } catch (err) {
            toast.error("Couldn't send your message. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <main data-testid="contact-page" className="bg-[#FAF8F5] pt-32 pb-20">
            <section className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="text-center max-w-3xl mx-auto">
                    <p className="font-handwriting text-3xl text-[#C5832B]">
                        Say hello
                    </p>
                    <h1 className="font-serif text-5xl md:text-6xl text-[#3E2723] mt-3 leading-tight">
                        Get in touch
                    </h1>
                    <p className="text-base text-[#6D4C41] mt-5 leading-relaxed">
                        Custom cake orders, partnership ideas, or just a kind word — we
                        love to hear from you.
                    </p>
                </div>

                <div className="mt-16 grid lg:grid-cols-12 gap-10">
                    {/* Form */}
                    <form
                        onSubmit={submit}
                        data-testid="contact-form"
                        className="lg:col-span-7 bg-white border border-[#E8D8C8] rounded-3xl p-8 md:p-10"
                    >
                        <div className="grid sm:grid-cols-2 gap-5">
                            <div>
                                <label className="text-xs uppercase tracking-[0.18em] text-[#6D4C41]">
                                    Your name
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={form.name}
                                    onChange={update("name")}
                                    data-testid="contact-input-name"
                                    placeholder="Aaditya Sharma"
                                    className="mt-2 w-full px-4 py-3 rounded-xl border border-[#E8D8C8] bg-[#FAF8F5] text-[#3E2723] focus:outline-none focus:border-[#C5832B] focus:ring-2 focus:ring-[#C5832B]/20 transition-all"
                                />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.18em] text-[#6D4C41]">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={update("email")}
                                    data-testid="contact-input-email"
                                    placeholder="you@email.com"
                                    className="mt-2 w-full px-4 py-3 rounded-xl border border-[#E8D8C8] bg-[#FAF8F5] text-[#3E2723] focus:outline-none focus:border-[#C5832B] focus:ring-2 focus:ring-[#C5832B]/20 transition-all"
                                />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.18em] text-[#6D4C41]">
                                    Phone (optional)
                                </label>
                                <input
                                    type="tel"
                                    value={form.phone}
                                    onChange={update("phone")}
                                    data-testid="contact-input-phone"
                                    placeholder="+91 9..."
                                    className="mt-2 w-full px-4 py-3 rounded-xl border border-[#E8D8C8] bg-[#FAF8F5] text-[#3E2723] focus:outline-none focus:border-[#C5832B] focus:ring-2 focus:ring-[#C5832B]/20 transition-all"
                                />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.18em] text-[#6D4C41]">
                                    Branch (optional)
                                </label>
                                <select
                                    value={form.branch}
                                    onChange={update("branch")}
                                    data-testid="contact-input-branch"
                                    className="mt-2 w-full px-4 py-3 rounded-xl border border-[#E8D8C8] bg-[#FAF8F5] text-[#3E2723] focus:outline-none focus:border-[#C5832B] focus:ring-2 focus:ring-[#C5832B]/20 transition-all"
                                >
                                    <option value="">Any branch</option>
                                    {BRANCHES.map((b) => (
                                        <option key={b.id} value={b.name}>
                                            {b.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="mt-5">
                            <label className="text-xs uppercase tracking-[0.18em] text-[#6D4C41]">
                                Your message
                            </label>
                            <textarea
                                required
                                rows={6}
                                value={form.message}
                                onChange={update("message")}
                                data-testid="contact-input-message"
                                placeholder="I'd love to order a cake for…"
                                className="mt-2 w-full px-4 py-3 rounded-xl border border-[#E8D8C8] bg-[#FAF8F5] text-[#3E2723] focus:outline-none focus:border-[#C5832B] focus:ring-2 focus:ring-[#C5832B]/20 transition-all resize-none"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="contact-submit-btn"
                            className="mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#3E2723] text-[#FAF8F5] text-sm hover:bg-[#C5832B] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {loading ? "Sending..." : "Send message"}
                            <Send className="w-4 h-4" />
                        </button>
                    </form>

                    {/* Info */}
                    <aside className="lg:col-span-5 space-y-5">
                        <div className="rounded-3xl bg-[#2A1A17] text-[#F3EBE1] p-8">
                            <p className="font-handwriting text-2xl text-[#C5832B] leading-none">
                                Customer care
                            </p>
                            <h3 className="font-serif text-3xl mt-2">We answer fast.</h3>
                            <ul className="mt-6 space-y-4 text-sm">
                                <li className="flex items-center gap-3">
                                    <span className="grid place-items-center w-9 h-9 rounded-full bg-[#C5832B] text-[#2A1A17] flex-shrink-0">
                                        <Headphones className="w-4 h-4" />
                                    </span>
                                    <a
                                        href={`tel:${COMPANY.customerCare.phone.replace(/[^\d+]/g, "")}`}
                                        data-testid="contact-customer-care-phone"
                                        className="hover:text-[#C5832B] transition-colors"
                                    >
                                        {COMPANY.customerCare.phone}
                                    </a>
                                </li>
                                <li className="flex items-center gap-3">
                                    <span className="grid place-items-center w-9 h-9 rounded-full bg-[#C5832B] text-[#2A1A17] flex-shrink-0">
                                        <Mail className="w-4 h-4" />
                                    </span>
                                    <a
                                        href={`mailto:${COMPANY.customerCare.email}`}
                                        data-testid="contact-customer-care-email"
                                        className="hover:text-[#C5832B] transition-colors break-all"
                                    >
                                        {COMPANY.customerCare.email}
                                    </a>
                                </li>
                                <li className="flex items-center gap-3">
                                    <span className="grid place-items-center w-9 h-9 rounded-full bg-[#C5832B] text-[#2A1A17] flex-shrink-0">
                                        <Phone className="w-4 h-4" />
                                    </span>
                                    <a
                                        href="tel:+917879402674"
                                        className="hover:text-[#C5832B] transition-colors"
                                    >
                                        +91 78794-02674 <span className="text-[#F3EBE1]/60">(Ambikapur)</span>
                                    </a>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="grid place-items-center w-9 h-9 rounded-full bg-[#C5832B] text-[#2A1A17] flex-shrink-0">
                                        <MapPin className="w-4 h-4" />
                                    </span>
                                    <span>
                                        Branches in Ambikapur, Surajpur and Bishrampur,
                                        Chhattisgarh.
                                    </span>
                                </li>
                            </ul>
                            <div className="flex items-center gap-3 mt-7">
                                <a
                                    href={COMPANY.instagram.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="contact-instagram"
                                    aria-label={`Instagram @${COMPANY.instagram.handle}`}
                                    className="inline-flex items-center gap-2 px-4 h-10 rounded-full border border-[#F3EBE1]/20 hover:bg-[#C5832B] hover:border-[#C5832B] hover:text-[#2A1A17] transition-colors text-xs"
                                >
                                    <Instagram className="w-4 h-4" />
                                    @{COMPANY.instagram.handle}
                                </a>
                            </div>
                        </div>

                        {/* Franchise */}
                        <div
                            data-testid="contact-franchise-card"
                            className="rounded-3xl bg-[#3E2723] text-[#F3EBE1] p-8"
                        >
                            <div className="flex items-start gap-3">
                                <span className="grid place-items-center w-10 h-10 rounded-full bg-[#C5832B] text-[#2A1A17] flex-shrink-0">
                                    <Briefcase className="w-4 h-4" />
                                </span>
                                <div>
                                    <p className="font-handwriting text-2xl text-[#C5832B] leading-none">
                                        Open a Baker's Basket?
                                    </p>
                                    <h3 className="font-serif text-2xl mt-1">
                                        Franchise enquiries
                                    </h3>
                                    <p className="text-sm text-[#F3EBE1]/75 mt-2 leading-relaxed">
                                        Interested in bringing The Baker's Basket to your
                                        town? We'd love to hear from you.
                                    </p>
                                    <a
                                        href="/franchise#franchise-form"
                                        data-testid="contact-franchise-cta"
                                        className="inline-flex items-center gap-2 mt-4 px-4 py-2.5 rounded-full bg-[#C5832B] text-[#2A1A17] text-xs hover:bg-[#FAF8F5] transition-colors"
                                    >
                                        <Mail className="w-3.5 h-3.5" />
                                        Fill the franchise form
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Company / Legal details */}
                        <div
                            data-testid="contact-company-details"
                            className="rounded-3xl bg-[#F3EBE1] border border-[#E8D8C8] p-8"
                        >
                            <p className="font-handwriting text-2xl text-[#C5832B] leading-none">
                                Registered details
                            </p>
                            <h3 className="font-serif text-2xl text-[#3E2723] mt-1">
                                Company information
                            </h3>
                            <ul className="mt-5 space-y-3 text-sm text-[#6D4C41]">
                                <li className="flex items-start gap-3">
                                    <Building2 className="w-4 h-4 text-[#C5832B] mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="uppercase tracking-[0.16em] text-[10px] text-[#6D4C41]/70">
                                            Firm name
                                        </p>
                                        <p className="text-[#3E2723]">{COMPANY.firmName}</p>
                                    </div>
                                </li>
                                <li className="flex items-start gap-3">
                                    <MapPin className="w-4 h-4 text-[#C5832B] mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="uppercase tracking-[0.16em] text-[10px] text-[#6D4C41]/70">
                                            Corporate office
                                        </p>
                                        <p
                                            data-testid="contact-corporate-office"
                                            className="text-[#3E2723] leading-relaxed"
                                        >
                                            {COMPANY.corporateOffice}
                                        </p>
                                    </div>
                                </li>
                                <li className="flex items-start gap-3">
                                    <FileText className="w-4 h-4 text-[#C5832B] mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="uppercase tracking-[0.16em] text-[10px] text-[#6D4C41]/70">
                                            GSTIN
                                        </p>
                                        <p className="text-[#3E2723] font-mono">{COMPANY.gstin}</p>
                                    </div>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Mail className="w-4 h-4 text-[#C5832B] mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="uppercase tracking-[0.16em] text-[10px] text-[#6D4C41]/70">
                                            Business email
                                        </p>
                                        <a
                                            href={`mailto:${COMPANY.email}`}
                                            data-testid="contact-business-email"
                                            className="text-[#3E2723] hover:text-[#C5832B] transition-colors break-all"
                                        >
                                            {COMPANY.email}
                                        </a>
                                    </div>
                                </li>
                            </ul>
                        </div>

                        <div className="rounded-3xl bg-[#F3EBE1] border border-[#E8D8C8] p-8">
                            <p className="font-handwriting text-2xl text-[#C5832B] leading-none">
                                A small note
                            </p>
                            <p className="text-sm text-[#6D4C41] mt-3 leading-relaxed">
                                Custom cake orders need at least 24 hours' notice. For
                                tiered or special-occasion cakes, please reach out 3–5 days
                                in advance so we can make it perfect.
                            </p>
                        </div>
                    </aside>
                </div>
            </section>
        </main>
    );
};

export default Contact;
